/**
 * Utility functions for consistent error message handling
 */

export interface GraphQLError {
  graphQLErrors?: Array<{ message: string }>;
  networkError?: { message: string };
  message?: string;
}

/**
 * Extracts a user-friendly error message from various error formats
 */
export const getErrorMessage = (error: unknown): string => {
  if (!error) {
    return 'An unexpected error occurred. Please try again.';
  }

  // Handle GraphQL errors
  if (typeof error === 'object' && 'graphQLErrors' in error) {
    const gqlError = error as GraphQLError;
    if (gqlError.graphQLErrors && gqlError.graphQLErrors.length > 0 && gqlError.graphQLErrors[0]) {
      return gqlError.graphQLErrors[0].message;
    }
    if (gqlError.networkError?.message) {
      return gqlError.networkError.message;
    }
    if (gqlError.message) {
      return gqlError.message;
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Fallback
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Logs an error with context for debugging
 */
export const logError = (error: unknown, context?: string): void => {
  const message = getErrorMessage(error);
  const prefix = context ? `[${context}]` : '';
  console.error(`${prefix} ${message}`, error);
};

